import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  Signal,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { ReportPeriodSelectorComponent } from '../report-period-selector/report-period-selector.component';
import { TsButtonModule, TsInputComponent, TsPageHeaderModule } from 'tsui';
import {
  AssetManagementFileSources,
  ReportPeriod,
  TrailingIncomeStatement,
  TrailingIncomeStatementEntry,
} from '../../models';
import { AssetManagementGridService } from '../../services/asset-management-grid.service';
import { environment } from '@env/environment';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { GridApi, GridOptions } from 'ag-grid-enterprise';

@Component({
  selector: 'trailing-income-statement-grid',
  standalone: true,
  imports: [
    MatMenuModule,
    ReportPeriodSelectorComponent,
    TsButtonModule,
    TsInputComponent,
    TsPageHeaderModule,
    AgGridAngular,
  ],
  templateUrl: './trailing-income-statement-grid.component.html',
  styleUrl: '../../styles/asset-management-shared-styles.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrailingIncomeStatementGridComponent {
  readonly trailingIncomeStatement = input.required<TrailingIncomeStatement>();
  readonly reportPeriod = input.required<ReportPeriod>();
  readonly loading = input.required<boolean>();

  @Output() reportPeriodChanged = new EventEmitter<ReportPeriod>();
  @Output() importBudget = new EventEmitter<{ file: File; source: AssetManagementFileSources }>();

  readonly assetManagementGridService = inject(AssetManagementGridService);

  protected readonly trailingIncomeStatementColDefs: Signal<ColDef[]> = computed(() => {
    const listOfPeriodsInRange: ColDef[] =
      this.trailingIncomeStatement()?.report_period_range?.map((period) => {
        const label = TrailingIncomeStatementGridComponent.formatPeriodLabel(period);
        const isActual = this.trailingIncomeStatement().actual_periods.includes(period);
        const labelSuffix = isActual ? ' (Actual)' : ' (Budget)';

        return {
          headerName: label + labelSuffix,
          field: `periods.${period}.amount`,
          aggFunc: 'sum',
          minWidth: 210,
          valueFormatter: ({ data }: { data: TrailingIncomeStatementEntry }) =>
            this.assetManagementGridService.formatCurrencyToAccounting(data?.periods?.[period]?.amount, true),
          ...AssetManagementGridService.getTreeDataGridCommonColDef(),
        };
      }) || [];

    const totalColumnDef: ColDef = {
      headerName: 'Total',
      field: 'total',
      aggFunc: 'sum',
      minWidth: 210,
      cellClass: 'font-semibold',
      valueFormatter: ({ data }: { data: TrailingIncomeStatementEntry }) =>
        this.assetManagementGridService.formatCurrencyToAccounting(data?.total, true),
      ...AssetManagementGridService.getTreeDataGridCommonColDef(),
    };

    return [...listOfPeriodsInRange, totalColumnDef];
  });
  protected readonly flattenedTrailingIncomeStatement: Signal<TrailingIncomeStatementEntry[]> = computed(() => {
    if (this.trailingIncomeStatement()) {
      return AssetManagementGridService.flattenTreeAndAddPath(this.trailingIncomeStatement().trailingIncomeStatement, {
        key: 'display',
        value: 'group',
      });
    } else {
      return [];
    }
  });
  protected readonly autoGroupColumnDef: ColDef<any> = AssetManagementGridService.getAutoGroupColDef('Account', 'name');
  protected readonly getDataPath = AssetManagementGridService.getDataPath;
  protected readonly gridOptions: GridOptions = AssetManagementGridService.getTreeDataGridCommonOptions();
  private gridApi!: GridApi<any>;

  get enableImport(): boolean {
    return environment?.env !== 'productionUI';
  }

  private static get periodToMonthMap(): { [key: string]: string } {
    return {
      '1': 'January',
      '2': 'February',
      '3': 'March',
      '4': 'April',
      '5': 'May',
      '6': 'June',
      '7': 'July',
      '8': 'August',
      '9': 'September',
      '10': 'October',
      '11': 'November',
      '12': 'December',
    };
  }

  protected onGridReady(params: GridReadyEvent<any>) {
    this.gridApi = params.api;

    AssetManagementGridService.autoSizeColumns(this.gridApi);
  }

  private static formatPeriodLabel(period: string) {
    const [month, year] = period.split('/');

    return `${TrailingIncomeStatementGridComponent.periodToMonthMap[month]} ${year}`;
  }
}
